"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import { ErrorMessage } from "@/shared/components/common/ErrorMessage";

/**
 * APP-LEVEL ERROR BOUNDARY (Main Error Catcher)
 *
 * Purpose: Automatically catches runtime errors in React components
 *
 * When this triggers:
 * - Component render crashes (e.g., accessing null.property)
 * - useEffect errors
 * - Event handler crashes
 * - Async errors in components
 * - Data fetching errors in client components
 * - Any unhandled error in pages/components
 *
 * What it catches:
 * All pages: /client/[id], /advisor, /give, etc.
 * All client components
 * All layouts EXCEPT root layout (see global-error.tsx)
 *
 * What it does NOT catch:
 * Root layout errors (caught by global-error.tsx)
 * Server-side errors (handled by serverGraphql.ts)
 * Mutation errors (handled by queryClient.ts)
 *
 * User experience:
 * - Shows friendly error message
 * - Provides "Try Again" button to recover
 * - Error is sent to Sentry for investigation
 */

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Captures ALL application crashes and sends to Sentry
    Sentry.captureException(error, {
      tags: {
        errorType: "application",
        location: "error-boundary",
      },
    });
  }, [error]);

  const handleAction = (): void => {
    reset();
    window.location.reload();
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-slate-50">
      <ErrorMessage
        title="Something went wrong"
        message="An error occurred. Please try refreshing the page."
        handleAction={handleAction}
        buttonText="Try Again"
      />
    </div>
  );
}
